import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Procurement } from '@/types/database.types';
import { format } from 'date-fns';
import { Package, ChevronDown, ChevronUp } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface QualityProcurementDetailsCardProps {
  procurement: Procurement;
}

const QualityProcurementDetailsCard: React.FC<QualityProcurementDetailsCardProps> = ({ procurement }) => {
  const [itemsExpanded, setItemsExpanded] = useState(false);
  
  const items = (procurement as any).items || [];
  const isMultiItem = items.length > 1;

  const getSourceName = () => {
    if (procurement.source_type === 'farm' && (procurement as any).farms) {
      return (procurement as any).farms.farmer_name;
    }
    if (procurement.source_type === 'supplier' && (procurement as any).suppliers) {
      return (procurement as any).suppliers.name;
    }
    if (procurement.source_type === 'agent' && (procurement as any).agents) {
      return (procurement as any).agents.name;
    }
    // Check for source_name directly
    if ((procurement as any).source_name) {
      return (procurement as any).source_name;
    }
    return 'Unknown';
  };

  const getLocation = () => {
    if (procurement.source_type === 'farm' && (procurement as any).farms) {
      return `${(procurement as any).farms.city || ''}, ${(procurement as any).farms.region || ''}`;
    }
    return 'N/A';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span>Procurement Details</span>
            {isMultiItem && (
              <Badge variant="outline" className="text-xs">
                <Package className="h-3 w-3 mr-1" />
                {items.length} items
              </Badge>
            )}
          </div>
          <Badge variant="secondary">{procurement.source_type}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Product</p>
            <p className="font-medium">{procurement.product_name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Quantity</p>
            <p className="font-medium">{procurement.total_quantity || 0} kg</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Source</p>
            <p className="font-medium">{getSourceName()}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Location</p>
            <p className="font-medium">{getLocation()}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Expected Delivery</p>
            <p className="font-medium">
              {procurement.expected_delivery_date 
                ? format(new Date(procurement.expected_delivery_date), 'MMM dd, yyyy')
                : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Quality Grade</p>
            <p className="font-medium">{procurement.quality_grade || 'Pending inspection'}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Total Amount</p>
            <p className="font-medium">Br {procurement.total_amount?.toLocaleString() || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Status</p>
            <Badge variant="outline">{procurement.status?.replace('_', ' ')}</Badge>
          </div>
        </div>

        {procurement.notes && (
          <div>
            <p className="text-sm text-muted-foreground">Notes</p>
            <p className="text-sm mt-1">{procurement.notes}</p>
          </div>
        )}

        {/* Item-level breakdown for multi-product procurements */}
        {isMultiItem && (
          <Collapsible open={itemsExpanded} onOpenChange={setItemsExpanded}>
            <CollapsibleTrigger asChild>
              <Button variant="outline" size="sm" className="w-full justify-between">
                <span className="flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  View {items.length} Products
                </span>
                {itemsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
              <div className="bg-muted/50 rounded-md p-3 space-y-2">
                {items.map((item: any) => {
                  const itemQuality = item.item_quality?.[0];
                  return (
                    <div key={item.id} className="flex justify-between items-center text-sm border-b border-border/50 pb-2 last:border-0 last:pb-0">
                      <div className="flex-1">
                        <span className="font-medium">{item.product_name}</span>
                        <span className="text-muted-foreground ml-2">({item.quantity} kg @ Br{item.price_per_unit}/kg)</span>
                      </div>
                      <div className="flex gap-2 items-center">
                        {item.quality_grade && (
                          <Badge variant="outline" className="text-xs">
                            {item.quality_grade}
                          </Badge>
                        )}
                        {itemQuality?.quality_grade && itemQuality.quality_grade !== item.quality_grade && (
                          <Badge variant="secondary" className="text-xs">
                            Inspected: {itemQuality.quality_grade}
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
};

export default QualityProcurementDetailsCard;
